/**
 * Hook for acquiring delegated access tokens for SharePoint operations.
 *
 * Uses MSAL to acquire tokens with the .default scope which triggers combined
 * consent for all permissions configured on the backend app (including Graph
 * permissions like Files.ReadWrite.All).
 *
 * IMPORTANT: AuthContext must call instance.setActiveAccount() before this
 * hook can work. Without that, getActiveAccount() always returns null.
 *
 * CRITICAL: Use .default scope (not access_as_user) to trigger combined consent
 * via knownClientApplications configuration.
 */

import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

// CRITICAL: Use .default scope to trigger combined consent for all permissions
// configured on the backend app (via knownClientApplications)
const DELEGATED_SCOPES = [
  `api://${import.meta.env.VITE_BACKEND_APP_CLIENT_ID}/.default`,
];

interface UseDelegatedTokenResult {
  /**
   * Acquire a delegated access token for SharePoint operations.
   * Returns null if no active account.
   * Falls back to popup if silent acquisition fails due to consent required.
   * Throws on other acquisition failures.
   */
  getDelegatedToken: () => Promise<string | null>;
}

export function useDelegatedToken(): UseDelegatedTokenResult {
  const { instance } = useMsal();

  const getDelegatedToken = useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount();

    if (!account) {
      // CRITICAL: If this is null, check that AuthContext calls setActiveAccount()
      // eslint-disable-next-line no-console
      console.warn('useDelegatedToken: No active account. Ensure AuthContext sets active account.');
      return null;
    }

    try {
      // Try silent token acquisition first
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: DELEGATED_SCOPES,
        account,
      });

      return tokenResponse.accessToken;
    } catch (error) {
      // CRITICAL: Handle InteractionRequiredAuthError with popup fallback
      // This happens when:
      // 1. User hasn't consented to the access_as_user scope yet
      // 2. MFA is required
      // 3. Token cache is empty or expired
      if (error instanceof InteractionRequiredAuthError) {
        // eslint-disable-next-line no-console
        console.info('Silent token acquisition failed, attempting popup...');
        try {
          const tokenResponse = await instance.acquireTokenPopup({
            scopes: DELEGATED_SCOPES,
            account,
          });

          return tokenResponse.accessToken;
        } catch (popupError) {
          // eslint-disable-next-line no-console
          console.error('Delegated token popup acquisition failed:', popupError);
          throw new Error('Failed to acquire delegated token via popup');
        }
      }

      // Re-throw other errors
      throw error;
    }
  }, [instance]);

  return { getDelegatedToken };
}
